import React, { useState } from 'react';
import { v4 as uuid } from 'uuid';
import ColorInput from './color-input';
import isValidHex from '../helpers/is-valid-hex';
import constants from '../constants/color-types';
import Tooltip from '../../tooltip';
import SimpleSelect from '../../simple-select';
import HorizontalStack from '../../horizontal-stack';
const { HEX_TYPE, RGB_TYPE, HSL_TYPE } = constants;

const TOOLTIP_ID = `color_picker_alpha_${uuid()}`;
const options = [
  { label: 'HEX', value: HEX_TYPE },
  { label: 'RGB', value: RGB_TYPE },
  { label: 'HSL', value: HSL_TYPE },
];

const defaultColorType = options[0];

const InputFields = ({
  hex,
  onChange,
  rgb,
  hsl,
  enableAlpha,
  alphaTooltipLabel = '',
  preventOverflow = false,
}) => {
  const [colorType, setColorType] = useState(defaultColorType);

  const isTooltipEnabled = !!alphaTooltipLabel;
  const tooltipAttrsForAlpha = {
    'data-tip': true,
    'data-for': TOOLTIP_ID,
  };

  const handleRGBChange = (data) => {
    const dataCopy = {};

    if (data.r !== undefined) {
      dataCopy.r = Number.parseInt(data.r, 10);
    }
    if (data.g !== undefined) {
      dataCopy.g = Number.parseInt(data.g, 10);
    }
    if (data.b !== undefined) {
      dataCopy.b = Number.parseInt(data.b, 10);
    }
    if (data.a !== undefined) {
      dataCopy.a = Number.parseInt(data.a, 10) / 100;
    }

    onChange({ ...rgb, ...dataCopy });
  };

  const handleHSLChange = (data) => {
    const dataCopy = {};

    if (data.h !== undefined) {
      dataCopy.h = Number.parseFloat(data.h);
    }
    if (data.s !== undefined) {
      dataCopy.s = Number.parseFloat(data.s) / 100;
    }
    if (data.l !== undefined) {
      dataCopy.l = Number.parseFloat(data.l) / 100;
    }

    onChange({ ...hsl, ...dataCopy });
  };

  const handleHEXChange = (data) => {
    if (isValidHex(data.hex)) {
      onChange(data.hex);
    }
  };

  const handleColorTypeChange = (value) => {
    setColorType(value);
  };

  return (
    <div className="ws-ui-color-picker__input-fields" translate="no">
      <div className="ws-ui-color-picker__color-type">
        <SimpleSelect
          transparent
          value={colorType}
          options={options}
          onChange={handleColorTypeChange}
          preventOverflow={preventOverflow}
        />
      </div>

      <HorizontalStack>
        {colorType.value === HEX_TYPE && (
          <div className="ws-ui-color-picker__hex-input">
            <ColorInput align="left" name={HEX_TYPE} value={hex} onChange={handleHEXChange} />
          </div>
        )}
        {colorType.value === RGB_TYPE && (
          <React.Fragment>
            <ColorInput name="r" min={0} max={255} value={rgb.r} onChange={handleRGBChange} />
            <ColorInput name="g" min={0} max={255} value={rgb.g} onChange={handleRGBChange} />
            <ColorInput name="b" min={0} max={255} value={rgb.b} onChange={handleRGBChange} />
          </React.Fragment>
        )}
        {colorType.value === HSL_TYPE && (
          <React.Fragment>
            <ColorInput
              asFloat
              name="h"
              min={0}
              max={360}
              value={hsl.h}
              onChange={handleHSLChange}
            />
            <ColorInput
              asFloat
              name="s"
              min={0}
              max={100}
              value={hsl.s * 100}
              onChange={handleHSLChange}
            />
            <ColorInput
              asFloat
              name="l"
              min={0}
              max={100}
              value={hsl.l * 100}
              onChange={handleHSLChange}
            />
          </React.Fragment>
        )}
        {enableAlpha && rgb.a !== undefined && (
          <div
            className="ws-ui-color-picker__alpha-input"
            {...(isTooltipEnabled && tooltipAttrsForAlpha)}
          >
            <ColorInput
              isValuePercent
              name="a"
              min={0}
              max={100}
              value={`${Math.round(rgb.a * 100)}%`}
              onChange={handleRGBChange}
            />
            {isTooltipEnabled && <Tooltip text={alphaTooltipLabel} id={TOOLTIP_ID} />}
          </div>
        )}
      </HorizontalStack>
    </div>
  );
};

export default InputFields;
